import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';
import {
  Assessment as AnalyticsIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  EventBusy as ExpiredIcon,
  AddCircle as NewIcon,
  TimerOff as ExpiringSoonIcon,
  DoNotDisturb as SuspendedIcon,
  ThumbDown as RejectedIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';

// Helper functions for metrics calculation
const getTrendIndicator = (trend, t) => {
  if (trend > 0) return { symbol: '↑', color: '#4caf50', label: t('reports.trendIncreased') };
  if (trend < 0) return { symbol: '↓', color: '#f44336', label: t('reports.trendDecreased') };
  return { symbol: '→', color: '#ff9800', label: t('reports.trendUnchanged') };
};

const calculateCompletionRate = (active, total) => {
  if (total === 0) return 0;
  return Math.round((active / total) * 100);
};

const calculateMetric = (stats) => {
  const total = stats?.overall_stats?.total_permits || 0;
  const active = stats?.overall_stats?.active_permits || 0;
  const expired = stats?.overall_stats?.expired_permits || 0;
  const pending = stats?.overall_stats?.pending_permits || 0;
  const cancelled = stats?.overall_stats?.cancelled_permits || 0;
  const inactive = stats?.overall_stats?.inactive_permits || 0;

  return {
    completionRate: calculateCompletionRate(active, total || 1),
    activePercentage: total > 0 ? Math.round((active / total) * 100) : 0,
    expiredPercentage: total > 0 ? Math.round((expired / total) * 100) : 0,
    pendingPercentage: total > 0 ? Math.round((pending / total) * 100) : 0,
    cancelledPercentage: total > 0 ? Math.round((cancelled / total) * 100) : 0,
    suspendedPercentage: total > 0 ? Math.round((inactive / total) * 100) : 0,
    averagePermitsPerDay: total > 0 ? (total / 30).toFixed(1) : 0,
  };
};

const Reports = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const orgContext = useOrg();
  const { user } = useAuth();
  const org = orgContext?.org;
  const orgCode = orgContext?.orgCode || localStorage.getItem('orgCode') || '';
  const orgDisplayName = org?.name || orgCode || 'Transport Authority';
  const orgType = org?.organization_type || '';

  const [tabValue, setTabValue] = useState(0);
  const [dashboardTabValue, setDashboardTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Statistics
  const [detailedStats, setDetailedStats] = useState(null);

  // Reports
  const [permitTypeReport, setPermitTypeReport] = useState([]);
  const [vehicleTypeReport, setVehicleTypeReport] = useState([]);
  const [authorityReport, setAuthorityReport] = useState([]);
  const [expiringReport, setExpiringReport] = useState([]);

  // History
  const [historyData] = useState([]);
  const [historyDialog, setHistoryDialog] = useState(false);


  // Re-fetch dashboard stats whenever the resolved org changes (tenant switch)
  useEffect(() => {
    if (orgContext?.loading) return; // wait until org is resolved
    fetchDetailedStats();
  }, [org, orgCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background poll for the Overview tab so newly created/updated permits
  // are reflected without a manual reload. Silent (no shared loading
  // spinner) and only runs while that tab is actually active, so it never
  // fights with the on-demand fetches for the other report tabs.
  useEffect(() => {
    if (orgContext?.loading) return;
    if (tabValue !== 0) return;
    let isPolling = false;
    const intervalId = setInterval(() => {
      if (document.hidden || isPolling) return;
      isPolling = true;
      fetchDetailedStats({ background: true }).finally(() => {
        isPolling = false;
      });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [tabValue, orgContext?.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetailedStats = async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const response = await apiClient.get('/permits/report_detailed_stats/');
      setDetailedStats(response.data);
      setError('');
    } catch (err) {
      if (!background) {
        setError(t('reports.loadStatsFailed'));
        console.error(err);
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  const fetchPermitTypeReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/permits/report_permits_by_type/');
      setPermitTypeReport(response.data.data);
      setError('');
    } catch (err) {
      setError(t('reports.loadPermitTypeFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleTypeReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/permits/report_permits_by_vehicle/');
      setVehicleTypeReport(response.data.data);
      setError('');
    } catch (err) {
      setError(t('reports.loadVehicleTypeFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthorityReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/permits/report_authority_summary/');
      setAuthorityReport(response.data.data);
      setError('');
    } catch (err) {
      setError(t('reports.loadAuthorityFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/permits/report_expiring_permits/?days=30');
      setExpiringReport(response.data.data);
      setError('');
    } catch (err) {
      setError(t('reports.loadExpiringFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchPermitTypeReport();
    } else if (newValue === 2) {
      fetchVehicleTypeReport();
    } else if (newValue === 3) {
      fetchAuthorityReport();
    } else if (newValue === 4) {
      fetchExpiringReport();
    }
  };

  // ── PDF download helpers ─────────────────────────────────────────────────

  const PDF_STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Inter, Arial, sans-serif; color: #1e293b; background: #fff; padding: 32px; font-size: 13px; }
    .pdf-header { background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%); color: #fff; padding: 24px 28px; border-radius: 10px; margin-bottom: 24px; }
    .pdf-header h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
    .pdf-header p { font-size: 12px; opacity: 0.85; }
    .pdf-header .meta { display: flex; gap: 24px; margin-top: 12px; font-size: 11px; opacity: 0.9; }
    h2 { font-size: 15px; font-weight: 700; color: #1976d2; margin: 20px 0 10px; border-left: 4px solid #1976d2; padding-left: 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
    thead tr { background: #1976d2; color: #fff; }
    thead th { padding: 9px 12px; text-align: left; font-weight: 600; }
    tbody tr:nth-child(even) { background: #f1f5f9; }
    tbody tr:hover { background: #e3f2fd; }
    tbody td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; }
    .stat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 12px; margin-bottom: 20px; }
    .stat-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
    .stat-box .label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
    .stat-box .value { font-size: 22px; font-weight: 700; color: #1e293b; }
    .stat-box .sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 600; }
    .badge-success { background: #dcfce7; color: #166534; }
    .badge-error   { background: #fee2e2; color: #991b1b; }
    .badge-warning { background: #fef9c3; color: #854d0e; }
    .badge-info    { background: #dbeafe; color: #1e40af; }
    .badge-grey    { background: #f1f5f9; color: #475569; }
    .progress-bar-wrap { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; margin-top: 4px; }
    .progress-bar-fill { height: 100%; background: #1976d2; border-radius: 4px; }
    .alert-warn { background: #fef9c3; border: 1px solid #fde047; border-radius: 6px; padding: 10px 14px; color: #854d0e; font-size: 12px; font-weight: 500; margin-bottom: 14px; }
    .section-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 14px; }
    .section-card .card-title { font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 10px; }
    .kv-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dotted #e2e8f0; font-size: 12px; }
    .kv-row .kv-key { color: #64748b; font-weight: 500; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
    @media print {
      body { padding: 16px; }
      .pdf-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      thead tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .badge, .badge-success, .badge-error, .badge-warning, .badge-info, .badge-grey
           { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .progress-bar-fill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const openPdfWindow = (title, bodyHTML) => {
    const now = new Date().toLocaleString('en-PK', {
      dateStyle: 'long', timeStyle: 'short',
    });
    const orgLabel = org?.name
      ? `${org.name}${orgType ? ' (' + orgType + ')' : ''}`
      : (orgCode || 'Transport Authority');
    const generatedBy = user?.username ? ` &bull; ${t('reports.pdfGeneratedBy', { name: user.first_name || user.username })}` : '';
    const html = `<!DOCTYPE html><html lang="en"><head>
      <meta charset="UTF-8">
      <title>${orgLabel} — ${title}</title>
      <style>${PDF_STYLES}</style>
    </head><body>
      <div class="pdf-header">
        <h1>${orgLabel} — ${title}</h1>
        <p>${t('reports.pdfSystemTag')}</p>
        <div class="meta">
          <span>${t('reports.pdfGeneratedLabel', { date: now })}</span>
          ${org?.city ? `<span>📍 ${org.city}${org.region ? ', ' + org.region : ''}</span>` : ''}
          <span${generatedBy}/>
        </div>
      </div>
      ${bodyHTML}
      <div class="footer">${t('reports.pdfFooterNote', { org: orgLabel, date: now })}</div>
    </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  const downloadPermitTypeReportPDF = () => {
    if (!permitTypeReport.length) return;
    const rows = permitTypeReport.map(item => {
      const pct = item.total > 0 ? Math.round((item.active / item.total) * 100) : 0;
      return `
        <tr>
          <td><strong>${item.permit_type}</strong></td>
          <td align="center">${item.total}</td>
          <td align="center"><span class="badge badge-success">${item.active}</span></td>
          <td align="center"><span class="badge badge-warning">${item.pending}</span></td>
          <td align="center"><span class="badge badge-error">${item.expired}</span></td>
          <td align="center"><span class="badge badge-error">${item.cancelled}</span></td>
          <td align="center"><span class="badge badge-grey">${item.inactive}</span></td>
          <td style="width:130px">
            <div>${pct}%</div>
            <div class="progress-bar-wrap"><div class="progress-bar-fill" style="width:${pct}%"></div></div>
          </td>
        </tr>`;
    }).join('');
    const totals = permitTypeReport.reduce((acc, r) => ({
      total: acc.total + (r.total || 0),
      active: acc.active + (r.active || 0),
      expired: acc.expired + (r.expired || 0),
      cancelled: acc.cancelled + (r.cancelled || 0),
      pending: acc.pending + (r.pending || 0),
      inactive: acc.inactive + (r.inactive || 0),
    }), { total: 0, active: 0, expired: 0, cancelled: 0, pending: 0, inactive: 0 });

    openPdfWindow(t('reports.pdfPermitTypeReportTitle'), `
      <div class="stat-grid" style="grid-template-columns: repeat(4, 1fr);">
        <div class="stat-box"><div class="label">${t('reports.pdfTotalTypes')}</div><div class="value">${permitTypeReport.length}</div></div>
        <div class="stat-box"><div class="label">${t('reports.pdfTotalPermits')}</div><div class="value">${totals.total}</div></div>
        <div class="stat-box"><div class="label">${t('common.active')}</div><div class="value" style="color:#166534">${totals.active}</div></div>
        <div class="stat-box"><div class="label">${t('common.expired')}</div><div class="value" style="color:#991b1b">${totals.expired}</div></div>
        <div class="stat-box"><div class="label">${t('common.pending')}</div><div class="value" style="color:#1e40af">${totals.pending}</div></div>
        <div class="stat-box"><div class="label">${t('common.cancelled')}</div><div class="value" style="color:#854d0e">${totals.cancelled}</div></div>
        <div class="stat-box"><div class="label">${t('common.inactive')}</div><div class="value" style="color:#475569">${totals.inactive}</div></div>
      </div>
      <h2>${t('reports.pdfBreakdownByPermitType')}</h2>
      <table>
        <thead><tr>
          <th>${t('reports.pdfColPermitType')}</th><th>${t('reports.pdfColTotal')}</th><th>${t('common.active')}</th><th>${t('common.pending')}</th>
          <th>${t('common.expired')}</th><th>${t('common.cancelled')}</th><th>${t('common.inactive')}</th><th>${t('reports.pdfColActiveRate')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`);
  };

  const downloadVehicleTypeReportPDF = () => {
    if (!vehicleTypeReport.length) return;
    const rows = vehicleTypeReport.map(item => {
      const pct = item.total > 0 ? Math.round((item.active / item.total) * 100) : 0;
      return `
        <tr>
          <td><strong>${item.vehicle_type}</strong></td>
          <td align="center">${item.total}</td>
          <td align="center"><span class="badge badge-success">${item.active}</span></td>
          <td align="center"><span class="badge badge-warning">${item.pending ?? 0}</span></td>
          <td align="center"><span class="badge badge-error">${item.expired}</span></td>
          <td align="center"><span class="badge badge-error">${item.cancelled ?? 0}</span></td>
          <td align="center"><span class="badge badge-grey">${item.inactive ?? 0}</span></td>
          <td style="width:130px">
            <div>${pct}%</div>
            <div class="progress-bar-wrap"><div class="progress-bar-fill" style="width:${pct}%"></div></div>
          </td>
        </tr>`;
    }).join('');
    const totals = vehicleTypeReport.reduce((acc, r) => ({
      total: acc.total + (r.total || 0),
      active: acc.active + (r.active || 0),
      pending: acc.pending + (r.pending || 0),
      expired: acc.expired + (r.expired || 0),
      cancelled: acc.cancelled + (r.cancelled || 0),
      inactive: acc.inactive + (r.inactive || 0),
    }), { total: 0, active: 0, pending: 0, expired: 0, cancelled: 0, inactive: 0 });

    openPdfWindow(t('reports.pdfVehicleTypeReportTitle'), `
      <div class="stat-grid" style="grid-template-columns: repeat(4, 1fr);">
        <div class="stat-box"><div class="label">${t('reports.pdfVehicleTypesLabel')}</div><div class="value">${vehicleTypeReport.length}</div></div>
        <div class="stat-box"><div class="label">${t('reports.pdfTotalPermits')}</div><div class="value">${totals.total}</div></div>
        <div class="stat-box"><div class="label">${t('common.active')}</div><div class="value" style="color:#166534">${totals.active}</div></div>
        <div class="stat-box"><div class="label">${t('common.expired')}</div><div class="value" style="color:#991b1b">${totals.expired}</div></div>
        <div class="stat-box"><div class="label">${t('common.pending')}</div><div class="value" style="color:#92400e">${totals.pending}</div></div>
        <div class="stat-box"><div class="label">${t('common.cancelled')}</div><div class="value" style="color:#991b1b">${totals.cancelled}</div></div>
        <div class="stat-box"><div class="label">${t('common.inactive')}</div><div class="value" style="color:#475569">${totals.inactive}</div></div>
      </div>
      <h2>${t('reports.pdfBreakdownByVehicleType')}</h2>
      <table>
        <thead><tr>
          <th>${t('reports.pdfColVehicleType')}</th><th>${t('reports.pdfColTotal')}</th><th>${t('common.active')}</th><th>${t('common.pending')}</th>
          <th>${t('common.expired')}</th><th>${t('common.cancelled')}</th><th>${t('common.inactive')}</th><th>${t('reports.pdfColActiveRate')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`);
  };

  const downloadAuthorityReportPDF = () => {
    if (!authorityReport.length) return;
    const rows = authorityReport.map(item => `
      <tr>
        <td><strong>${item.authority}</strong></td>
        <td align="center">${item.total_permits}</td>
        <td align="center"><span class="badge badge-success">${item.active}</span></td>
        <td align="center"><span class="badge badge-warning">${item.pending ?? 0}</span></td>
        <td align="center"><span class="badge badge-error">${item.expired}</span></td>
        <td align="center"><span class="badge badge-error">${item.cancelled ?? 0}</span></td>
        <td align="center"><span class="badge badge-grey">${item.inactive ?? 0}</span></td>
      </tr>`).join('');
    const totals = authorityReport.reduce((acc, r) => ({
      total: acc.total + (r.total_permits || 0),
      active: acc.active + (r.active || 0),
      pending: acc.pending + (r.pending || 0),
      expired: acc.expired + (r.expired || 0),
      cancelled: acc.cancelled + (r.cancelled || 0),
      inactive: acc.inactive + (r.inactive || 0),
    }), { total: 0, active: 0, pending: 0, expired: 0, cancelled: 0, inactive: 0 });

    openPdfWindow(t('reports.pdfAuthorityReportTitle'), `
      <div class="stat-grid" style="grid-template-columns: repeat(4, 1fr);">
        <div class="stat-box"><div class="label">${t('reports.pdfAuthoritiesLabel')}</div><div class="value">${authorityReport.length}</div></div>
        <div class="stat-box"><div class="label">${t('reports.pdfTotalPermits')}</div><div class="value">${totals.total}</div></div>
        <div class="stat-box"><div class="label">${t('common.active')}</div><div class="value" style="color:#166534">${totals.active}</div></div>
        <div class="stat-box"><div class="label">${t('common.expired')}</div><div class="value" style="color:#991b1b">${totals.expired}</div></div>
        <div class="stat-box"><div class="label">${t('common.pending')}</div><div class="value" style="color:#92400e">${totals.pending}</div></div>
        <div class="stat-box"><div class="label">${t('common.cancelled')}</div><div class="value" style="color:#991b1b">${totals.cancelled}</div></div>
        <div class="stat-box"><div class="label">${t('common.inactive')}</div><div class="value" style="color:#475569">${totals.inactive}</div></div>
      </div>
      <h2>${t('reports.pdfPermitsByAuthority')}</h2>
      <table>
        <thead><tr>
          <th>${t('reports.authorityCol')}</th><th>${t('reports.pdfColTotal')}</th><th>${t('common.active')}</th><th>${t('common.pending')}</th>
          <th>${t('common.expired')}</th><th>${t('common.cancelled')}</th><th>${t('common.inactive')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`);
  };

  const downloadExpiringReportPDF = () => {
    if (!expiringReport.length) return;
    const rows = expiringReport.map(item => {
      const daysLeft = Math.ceil((new Date(item.valid_to) - new Date()) / (1000 * 60 * 60 * 24));
      const badgeCls = daysLeft <= 7 ? 'badge-error' : daysLeft <= 15 ? 'badge-warning' : 'badge-grey';
      const permitTypeName = item.permit_type && typeof item.permit_type === 'object'
        ? item.permit_type.name : (item.permit_type || '—');
      return `
        <tr>
          <td><strong>${item.permit_number || '—'}</strong></td>
          <td>${item.vehicle_number || '—'}</td>
          <td>${item.owner_name || '—'}</td>
          <td>${item.owner_phone || '—'}</td>
          <td>${item.owner_cnic || '—'}</td>
          <td>${permitTypeName}</td>
          <td>${item.valid_from || '—'}</td>
          <td>${item.valid_to || '—'}</td>
          <td align="center"><span class="badge ${badgeCls}">${t('permitSearch.daysUnit', { count: daysLeft })}</span></td>
        </tr>`;
    }).join('');

    openPdfWindow(t('reports.pdfExpiringReportTitle'), `
      <div class="alert-warn">${t('reports.pdfWarningExpiring', { count: expiringReport.length })}</div>
      <h2>${t('reports.pdfExpiringDetail')}</h2>
      <table>
        <thead><tr>
          <th>${t('reports.permitNumberCol')}</th><th>${t('reports.vehicleCol')}</th><th>${t('reports.pdfColOwnerName')}</th><th>${t('reports.pdfColPhone')}</th>
          <th>${t('reports.pdfColCnic')}</th><th>${t('reports.typeCol')}</th><th>${t('reports.pdfColValidFrom')}</th><th>${t('reports.validToCol')}</th><th>${t('reports.daysLeftCol')}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`);
  };

  // KPI Card Component with Dark Text
  const KPICard = ({ title, value, subtext, trend, icon: Icon, gradient, metrics }) => {
    const trendInfo = getTrendIndicator(trend, t);
    return (
      <Card
        sx={{
          background: gradient,
          color: '#fff',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          },
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 0.5 }}>
            <Box>
              <Typography variant="overline" sx={{ fontSize: '0.65rem', opacity: 0.8, display: 'block', letterSpacing: '0.5px', color: '#fff', fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#fff', lineHeight: 1.2 }}>
                {value}
              </Typography>
            </Box>
            <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon sx={{ fontSize: 28, color: '#fff', opacity: 0.9 }} />
            </Box>
          </Box>

          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold' }}>
                {trendInfo.symbol} {Math.abs(trend)}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {trendInfo.label}
              </Typography>
            </Box>
          )}

          {subtext && (
            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', color: 'rgba(255,255,255,0.9)', fontSize: '0.68rem' }}>
              {subtext}
            </Typography>
          )}

          {metrics && (
            <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 'rgba(255,255,255,0.25) 1px solid' }}>
              {Object.entries(metrics).map(([key, val]) => (
                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ opacity: 0.8, color: 'rgba(255,255,255,0.85)', fontSize: '0.68rem' }}>{key}:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.68rem' }}>{val}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Dashboard Tab
  const renderDashboard = () => {
    const metrics = calculateMetric(detailedStats) || {};

    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Dashboard Sub-Tabs */}
            <Paper sx={{ mb: 2 }}>
              <Tabs
                value={dashboardTabValue}
                onChange={(e, newValue) => setDashboardTabValue(newValue)}
                sx={{
                  borderBottom: '1px solid #ddd',
                  '& .MuiTab-root': { textTransform: 'none', fontSize: '0.875rem', fontWeight: 500, minHeight: 42 },
                }}
              >
                <Tab label={t('reports.overviewTab')} />
                <Tab label={t('reports.analyticsTab')} />
              </Tabs>
            </Paper>

            {/* ── OVERVIEW TAB ── */}
            {dashboardTabValue === 0 && (
              <>
                {/* KPI Cards — Status (all 6 flow in one row when space allows) */}
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.totalPermits')}
                      value={detailedStats?.overall_stats?.total_permits ?? 0}
                      subtext={t('reports.perDayAvg', { rate: metrics?.averagePermitsPerDay || '0' })}
                      icon={AnalyticsIcon}
                      gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      metrics={{ [t('reports.thisMonth')]: detailedStats?.recent_activity?.created_last_30_days ?? 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.activePermits')}
                      value={detailedStats?.overall_stats?.active_permits ?? 0}
                      subtext={t('reports.pctOfTotal', { pct: metrics?.activePercentage || 0 })}
                      icon={CheckCircleIcon}
                      gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                      metrics={{ [t('reports.completionRate')]: `${metrics?.completionRate || 0}%` }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.pendingPermits')}
                      value={detailedStats?.overall_stats?.pending_permits ?? 0}
                      subtext={t('reports.pctOfTotal', { pct: metrics?.pendingPercentage || 0 })}
                      icon={PendingIcon}
                      gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      metrics={{ [t('common.pending')]: `${metrics?.pendingPercentage || 0}%` }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.expiredPermits')}
                      value={detailedStats?.overall_stats?.expired_permits ?? 0}
                      subtext={t('reports.pctOfTotal', { pct: metrics?.expiredPercentage || 0 })}
                      icon={ExpiredIcon}
                      gradient="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                      metrics={{ [t('common.expired')]: `${metrics?.expiredPercentage || 0}%` }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.rejectedPermits')}
                      value={detailedStats?.overall_stats?.cancelled_permits ?? 0}
                      subtext={t('reports.pctOfTotal', { pct: metrics?.cancelledPercentage || 0 })}
                      icon={RejectedIcon}
                      gradient="linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                      metrics={{ [t('common.cancelled')]: `${metrics?.cancelledPercentage || 0}%` }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <KPICard
                      title={t('reports.suspendedPermits')}
                      value={detailedStats?.overall_stats?.inactive_permits ?? 0}
                      subtext={t('reports.pctOfTotal', { pct: metrics?.suspendedPercentage || 0 })}
                      icon={SuspendedIcon}
                      gradient="linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
                      metrics={{ [t('common.inactive')]: `${metrics?.suspendedPercentage || 0}%` }}
                    />
                  </Grid>
                </Grid>

                {/* KPI Cards — Row 3: Activity */}
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                      title={t('reports.expiringSoon30d')}
                      value={detailedStats?.recent_activity?.expiring_in_30_days ?? 0}
                      subtext={t('reports.requiresAttention')}
                      icon={ExpiringSoonIcon}
                      gradient="linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)"
                      metrics={{ [t('reports.withinDays')]: detailedStats?.recent_activity?.expiring_in_30_days ?? 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                      title={t('reports.recentlyCreated')}
                      value={detailedStats?.recent_activity?.created_last_30_days ?? 0}
                      subtext={t('reports.last30Days')}
                      icon={NewIcon}
                      gradient="linear-gradient(135deg, #00b09b 0%, #96c93d 100%)"
                      metrics={{ [t('reports.thisWeek')]: Math.round((detailedStats?.recent_activity?.created_last_30_days ?? 0) / 4) }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KPICard
                      title={t('reports.recentlyModified')}
                      value={detailedStats?.recent_activity?.modified_last_30_days ?? 0}
                      subtext={t('reports.last30Days')}
                      icon={TrendingUpIcon}
                      gradient="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                      metrics={{ [t('reports.thisWeek')]: Math.round((detailedStats?.recent_activity?.modified_last_30_days ?? 0) / 4) }}
                    />
                  </Grid>
                </Grid>

                {/* Performance Metrics */}
                <Card sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', mb: 1.5 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                      {t('reports.kpiTitle')}
                    </Typography>
                    <Grid container spacing={3}>
                      {[
                        { label: t('reports.completionRate'), value: metrics?.completionRate || 0, color: 'linear-gradient(90deg,#22c55e,#16a34a)' },
                        { label: t('reports.activePermits'), value: metrics?.activePercentage || 0, color: 'linear-gradient(90deg,#22c55e,#16a34a)' },
                        { label: t('common.pending'), value: Math.min(metrics?.pendingPercentage || 0, 100), color: 'linear-gradient(90deg,#f59e0b,#d97706)' },
                        { label: t('common.expired'), value: Math.min(metrics?.expiredPercentage || 0, 100), color: 'linear-gradient(90deg,#ef4444,#dc2626)' },
                        { label: t('common.cancelled'), value: Math.min(metrics?.cancelledPercentage || 0, 100), color: 'linear-gradient(90deg,#dc2626,#b91c1c)' },
                        { label: t('common.inactive'), value: Math.min(metrics?.suspendedPercentage || 0, 100), color: 'linear-gradient(90deg,#6b7280,#4b5563)' },
                      ].map(({ label, value, color }) => (
                        <Grid item xs={12} sm={6} md={4} key={label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>{label}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1e293b' }}>{value}%</Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={value}
                            sx={{ width: '100%', height: 10, borderRadius: 5, backgroundColor: '#d1d5db', '& .MuiLinearProgress-bar': { background: color, borderRadius: 5 } }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* ── ANALYTICS TAB ── */}
            {dashboardTabValue === 1 && (
              <Box>
                {/* Row 1: Pie chart + Authority bar */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Card sx={{ height: '100%' }}>
                      <CardHeader title={t('reports.permitsByStatus')} sx={{ bgcolor: '#f5f5f5', py: 1, '& .MuiCardHeader-title': { fontSize: '0.95rem', fontWeight: 700 } }} />
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {(() => {
                          const STATUS_PIE_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#dc2626', '#6b7280'];
                          const pieData = [
                            { name: t('common.active'),    value: detailedStats?.overall_stats?.active_permits    || 0 },
                            { name: t('common.pending'),   value: detailedStats?.overall_stats?.pending_permits   || 0 },
                            { name: t('common.expired'),   value: detailedStats?.overall_stats?.expired_permits   || 0 },
                            { name: t('common.cancelled'), value: detailedStats?.overall_stats?.cancelled_permits || 0 },
                            { name: t('common.inactive'),  value: detailedStats?.overall_stats?.inactive_permits  || 0 },
                          ].filter(item => item.value > 0);
                          const total = pieData.reduce((s, d) => s + d.value, 0);
                          return (
                            <Box sx={{ position: 'relative' }}>
                              <ResponsiveContainer width="99%" height={isMobile ? 340 : 280}>
                                <PieChart>
                                  <Pie
                                    data={pieData}
                                    cx={isMobile ? "50%" : "40%"}
                                    cy={isMobile ? "39%" : "50%"}
                                    innerRadius={isMobile ? 55 : 75}
                                    outerRadius={isMobile ? 80 : 110}
                                    paddingAngle={3}
                                    dataKey="value"
                                  >
                                    {pieData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={STATUS_PIE_COLORS[index % STATUS_PIE_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, name) => [`${value} permits (${total > 0 ? Math.round((value / total) * 100) : 0}%)`, name]} />
                                  <Legend
                                    layout={isMobile ? "horizontal" : "vertical"}
                                    align={isMobile ? "center" : "right"}
                                    verticalAlign={isMobile ? "bottom" : "middle"}
                                    iconType="circle"
                                    iconSize={10}
                                    formatter={(value, entry) => (
                                      <span style={{ color: '#1e293b', fontSize: '0.82rem' }}>
                                        {value} <span style={{ color: '#64748b' }}>({total > 0 ? Math.round((entry.payload.value / total) * 100) : 0}%)</span>
                                      </span>
                                    )}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                              {/* Center label — positioned over the donut hole */}
                              <Box sx={{
                                position: 'absolute',
                                top: isMobile ? '39%' : '50%',
                                left: isMobile ? '50%' : '40%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                pointerEvents: 'none',
                              }}>
                                <Typography sx={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#1e293b', lineHeight: 1 }}>{total}</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.3 }}>{t('reports.total')}</Typography>
                              </Box>
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Card sx={{ height: '100%' }}>
                      <CardHeader title={t('reports.authorityDistribution')} sx={{ bgcolor: '#f5f5f5', py: 1, '& .MuiCardHeader-title': { fontSize: '0.95rem', fontWeight: 700 } }} />
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <ResponsiveContainer width="99%" height={240}>
                          <BarChart
                            data={detailedStats?.by_authority ? Object.entries(detailedStats.by_authority).map(([key, value]) => ({ name: key, permits: value })) : []}
                            margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="permits" fill="#667eea" name={t('reports.permitsLegend')} radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Row 2: Permit Types + Vehicle Types */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Card>
                      <CardHeader title={t('reports.permitTypesDistribution')} sx={{ bgcolor: '#f5f5f5', py: 1, '& .MuiCardHeader-title': { fontSize: '0.95rem', fontWeight: 700 } }} />
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <ResponsiveContainer width="99%" height={240}>
                          <BarChart
                            data={detailedStats?.by_permit_type ? Object.entries(detailedStats.by_permit_type).map(([key, value]) => ({ name: key, permits: value })) : []}
                            layout="vertical"
                            margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="permits" fill="#11998e" name={t('reports.permitsLegend')} radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Card>
                      <CardHeader title={t('reports.vehicleTypesDistribution')} sx={{ bgcolor: '#f5f5f5', py: 1, '& .MuiCardHeader-title': { fontSize: '0.95rem', fontWeight: 700 } }} />
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <ResponsiveContainer width="99%" height={240}>
                          <BarChart
                            data={detailedStats?.by_vehicle_type ? Object.entries(detailedStats.by_vehicle_type).map(([key, value]) => ({ name: key, permits: value })) : []}
                            layout="vertical"
                            margin={{ left: 10, right: 20, top: 10, bottom: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="permits" fill="#f7971e" name={t('reports.permitsLegend')} radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  // Permit Type Report Tab
  const renderPermitTypeReport = () => (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : permitTypeReport.length > 0 ? (
        <Grid container spacing={3}>
          {permitTypeReport.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardHeader
                  title={item.permit_type}
                  subheader={t('reports.codeLabel', { code: item.permit_code })}
                  action={<Chip label={t('reports.totalChip', { count: item.total })} color="primary" />}
                />
                <CardContent>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                      {t('reports.activeCount', { count: item.active })}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(item.active / Math.max(item.total, 1)) * 100}
                      sx={{ mt: 0.5, mb: 2, height: 7, borderRadius: 4, backgroundColor: '#e5e7eb', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#22c55e,#16a34a)', borderRadius: 4 } }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                        {t('reports.expiredCount', { count: item.expired })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                        {t('reports.rejectedCount', { count: item.cancelled })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                        {t('reports.pendingCount', { count: item.pending })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('reports.suspendedCount', { count: item.inactive })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadPermitTypeReportPDF}
            >
              {t('reports.downloadPdf')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Typography>{t('reports.noData')}</Typography>
      )}
    </Box>
  );

  // Vehicle Type Report Tab
  const renderVehicleTypeReport = () => (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : vehicleTypeReport.length > 0 ? (
        <Grid container spacing={3}>
          {vehicleTypeReport.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardHeader
                  title={item.vehicle_type}
                  action={<Chip label={t('reports.totalChip', { count: item.total })} color="primary" />}
                />
                <CardContent>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                      {t('reports.activeCount', { count: item.active })}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(item.active / Math.max(item.total, 1)) * 100}
                      sx={{ mt: 0.5, mb: 2, height: 7, borderRadius: 4, backgroundColor: '#e5e7eb', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#22c55e,#16a34a)', borderRadius: 4 } }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                        {t('reports.expiredCount', { count: item.expired })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                        {t('reports.rejectedCount', { count: item.cancelled ?? 0 })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                        {t('reports.pendingCount', { count: item.pending ?? 0 })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('reports.suspendedCount', { count: item.inactive ?? 0 })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadVehicleTypeReportPDF}
            >
              {t('reports.downloadPdf')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Typography>{t('reports.noData')}</Typography>
      )}
    </Box>
  );

  // Authority Report Tab
  const renderAuthorityReport = () => (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : authorityReport.length > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>{t('reports.authorityCol')}</strong></TableCell>
                    <TableCell align="right"><strong>{t('reports.total')}</strong></TableCell>
                    <TableCell align="right"><strong sx={{ color: '#22c55e' }}>{t('common.active')}</strong></TableCell>
                    <TableCell align="right"><strong sx={{ color: '#f59e0b' }}>{t('common.pending')}</strong></TableCell>
                    <TableCell align="right"><strong sx={{ color: '#ef4444' }}>{t('common.expired')}</strong></TableCell>
                    <TableCell align="right"><strong sx={{ color: '#dc2626' }}>{t('common.cancelled')}</strong></TableCell>
                    <TableCell align="right"><strong sx={{ color: '#6b7280' }}>{t('common.inactive')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {authorityReport.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell><strong>{item.authority}</strong></TableCell>
                      <TableCell align="right">{item.total_permits}</TableCell>
                      <TableCell align="right">
                        <Chip label={item.active} size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={item.pending} size="small" sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={item.expired} size="small" sx={{ bgcolor: '#ef4444', color: '#fff', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={item.cancelled} size="small" sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={item.inactive} size="small" sx={{ bgcolor: '#6b7280', color: '#fff', fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadAuthorityReportPDF}
            >
              {t('reports.downloadPdf')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Typography>{t('reports.noData')}</Typography>
      )}
    </Box>
  );

  // Expiring Permits Tab
  const renderExpiringReport = () => (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : expiringReport.length > 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="warning">
              {t('reports.expiringAlert', { count: expiringReport.length })}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>{t('reports.permitNumberCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.vehicleCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.ownerCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.typeCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.validToCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.daysLeftCol')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiringReport.map((item) => {
                    const daysLeft = Math.ceil(
                      (new Date(item.valid_to) - new Date()) / (1000 * 60 * 60 * 24)
                    );
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.permit_number}</TableCell>
                        <TableCell>{item.vehicle_number}</TableCell>
                        <TableCell>{item.owner_name}</TableCell>
                        <TableCell>
                          {item.permit_type && typeof item.permit_type === 'object'
                            ? item.permit_type.name
                            : item.permit_type}
                        </TableCell>
                        <TableCell>{item.valid_to}</TableCell>
                        <TableCell>
                          <Chip
                            label={t('permitSearch.daysUnit', { count: daysLeft })}
                            color={daysLeft <= 7 ? 'error' : daysLeft <= 15 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadExpiringReportPDF}
            >
              {t('reports.downloadPdf')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Typography>{t('reports.noExpiringSoon')}</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, minWidth: 0 }}>
      {/* ── Org-aware page header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
          {t('reports.pageTitle', { org: orgDisplayName })}
        </Typography>
        {(org?.city || orgType) && (
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            {[orgType, org?.city, org?.region].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: '1px solid #ddd',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
            },
          }}
        >
          <Tab label={t('reports.dashboardTab')} icon={<AnalyticsIcon />} iconPosition="start" />
          <Tab label={t('reports.permitTypesTab')} icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label={t('reports.vehicleTypesTab')} icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label={t('reports.authoritySummaryTab')} icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label={t('reports.expiringPermitsTab')} icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tabValue === 0 && renderDashboard()}
          {tabValue === 1 && renderPermitTypeReport()}
          {tabValue === 2 && renderVehicleTypeReport()}
          {tabValue === 3 && renderAuthorityReport()}
          {tabValue === 4 && renderExpiringReport()}
        </Box>
      </Paper>

      {/* History Dialog */}
      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('reports.permitHistory')}</DialogTitle>
        <DialogContent dividers>
          {historyData.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>{t('reports.actionCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.byCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.dateCol')}</strong></TableCell>
                    <TableCell><strong>{t('reports.notesCol')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip label={item.action} variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>{item.performed_by}</TableCell>
                      <TableCell>{new Date(item.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{item.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>{t('reports.noHistory')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
